import { api } from "@/lib/apiClient";

export type ArtikelImportQuelle = "datanorm" | "excel";

export type ArtikelImportAktion =
  | "neu"
  | "aktualisieren"
  | "unveraendert"
  | "uebersprungen"
  | "konflikt";

export const IMPORT_AKTION_LABELS: Record<ArtikelImportAktion, string> = {
  neu: "Neu",
  aktualisieren: "Aktualisieren",
  unveraendert: "Unverändert",
  uebersprungen: "Übersprungen",
  konflikt: "Konflikt",
};

export interface ArtikelImportJob {
  id: number;
  quelle: ArtikelImportQuelle;
  status: "neu" | "mapping" | "vorschau" | "importiert" | "fehler";
  dateiname: string | null;
  lieferant: string | null;
  konfiguration: Record<string, unknown> | null;
  zusammenfassung: Record<string, number> | null;
  created_by: string;
  created_at: string;
  committed_at: string | null;
}

export interface ArtikelImportAenderung {
  feld: string;
  alt: string | number | boolean | null;
  neu: string | number | boolean | null;
}

export interface ArtikelImportZeile {
  id: number;
  zeile_nr: number;
  artikel_id: number | null;
  aktion: ArtikelImportAktion | null;
  hinweis: string | null;
  daten: Record<string, unknown> | null;
  aenderungen: ArtikelImportAenderung[] | null;
}

export interface ArtikelImportUploadResult {
  job: ArtikelImportJob;
  headers: string[] | null;
  beispiele: (string | number | null)[][] | null;
}

export interface ArtikelImportJobResult {
  job: ArtikelImportJob;
  rows: ArtikelImportZeile[];
  total: number;
  limit: number;
  offset: number;
}

/** Zielfelder für das Excel-Spalten-Mapping (Reihenfolge = Anzeige). */
export const IMPORT_ZIELFELDER: { value: string; label: string }[] = [
  { value: "artikelnummer", label: "Artikelnummer" },
  { value: "name", label: "Name" },
  { value: "beschreibung", label: "Beschreibung" },
  { value: "einheit", label: "Einheit" },
  { value: "ean", label: "EAN" },
  { value: "matchcode", label: "Matchcode" },
  { value: "hersteller", label: "Hersteller" },
  { value: "hersteller_nr", label: "Hersteller-Nr." },
  { value: "lieferanten_nr", label: "Lieferanten-Nr." },
  { value: "warengruppe", label: "Warengruppe / Kategorie" },
  { value: "preis_brutto", label: "Bruttopreis (EK)" },
  { value: "rabatt", label: "Rabatt %" },
  { value: "preis_netto", label: "Nettopreis (EK)" },
  { value: "aufschlag", label: "Aufschlag %" },
  { value: "verkaufspreis", label: "Verkaufspreis" },
  { value: "preismenge", label: "Preismenge" },
  { value: "preis_einheit", label: "Preiseinheit" },
  { value: "preis_gueltig_bis", label: "Gültig bis" },
];

export const IMPORT_FELD_LABELS: Record<string, string> = {
  ...Object.fromEntries(IMPORT_ZIELFELDER.map((f) => [f.value, f.label])),
  // Datanorm-Vorschau (nur Anzeige, kein Commit-Feld)
  hauptwarengruppe: "Hauptwarengruppe",
  datanorm_warengruppe: "Warengruppe",
  kategorie_beibehalten: "Kategorie",
};

export type ImportMatchFeld = "lieferanten_artnr" | "hero_nr" | "ean";

export const IMPORT_MATCH_FELDER: { value: ImportMatchFeld; label: string }[] = [
  { value: "lieferanten_artnr", label: "Lieferanten-Artikelnummer (empfohlen)" },
  { value: "hero_nr", label: "Hero-Nr." },
  { value: "ean", label: "EAN" },
];

export async function uploadArtikelImport(
  files: File[],
  quelle: ArtikelImportQuelle,
  lieferant?: string,
  gueltigBis?: string,
): Promise<ArtikelImportUploadResult> {
  const form = new FormData();
  for (const file of files) form.append("files", file);
  form.append("quelle", quelle);
  form.append("lieferant", lieferant ?? "");
  form.append("gueltig_bis", gueltigBis?.trim() ?? "");
  return api.postForm<ArtikelImportUploadResult>("/artikel/import", form);
}

export async function setArtikelImportMapping(
  jobId: number,
  params: { mapping: Record<string, string>; matchFeld: ImportMatchFeld; lieferant?: string },
): Promise<{ job: ArtikelImportJob }> {
  return api.post<{ job: ArtikelImportJob }>(
    `/artikel/import/${jobId}/mapping`,
    {
      mapping: params.mapping,
      match_feld: params.matchFeld,
      lieferant: params.lieferant ?? null,
    },
    { timeoutMs: 120_000 },
  );
}

export async function getArtikelImportJob(
  jobId: number,
  params: { aktion?: string; limit?: number; offset?: number } = {},
): Promise<ArtikelImportJobResult> {
  const usp = new URLSearchParams();
  if (params.aktion) usp.set("aktion", params.aktion);
  if (params.limit != null) usp.set("limit", String(params.limit));
  if (params.offset != null) usp.set("offset", String(params.offset));
  const qs = usp.toString();
  return api.get<ArtikelImportJobResult>(
    qs ? `/artikel/import/${jobId}?${qs}` : `/artikel/import/${jobId}`,
  );
}

export async function commitArtikelImport(jobId: number): Promise<{ job: ArtikelImportJob }> {
  return api.post<{ job: ArtikelImportJob }>(`/artikel/import/${jobId}/commit`, undefined, {
    timeoutMs: 0,
  });
}

export async function deleteArtikelImportJob(jobId: number): Promise<void> {
  await api.del(`/artikel/import/${jobId}`);
}
